/**
 * 첨부 PDF 다운로드 선택 모달.
 *
 * 표 컬럼 PDF 아이콘 클릭 → 본 모달이 열려 listAttachments 로 메타 목록을 가져오고:
 *   - 1개: 별도 모달 안 띄우고 곧바로 새 탭 (호출측에서 처리). 본 컴포넌트는 2+ 인 경우만 띄움.
 *   - 2개+: 파일명 리스트 표시. 클릭 시 해당 파일 다운로드.
 *
 * 사용 패턴 (호출측):
 *   const metas = await listAttachments(parentKind, parentId, kind);
 *   if (metas.length === 1) openAttachmentInNewTab(metas[0].id);
 *   else if (metas.length > 1) setSelected({parentKind, parentId, kind});
 *   <AttachmentDownloadModal ... />
 */

import { useQuery } from '@tanstack/react-query';
import {
  listAttachments,
  previewAttachment,
  downloadAttachment,
  type AttachmentParentKind,
} from '../lib/attachment-api';
import { LoadingLines } from './ui/Skeleton';

interface Props {
  parentKind: AttachmentParentKind;
  parentId: string;
  kind: string;
  title?: string;
  onClose: () => void;
  /** 겹침 순서 — 다른 모달(예: 판보 모달 z-60) 위에서 열릴 때 높여 지정. 기본 50. */
  zIndex?: number;
}

export function AttachmentDownloadModal({ parentKind, parentId, kind, title, onClose, zIndex = 50 }: Props) {
  const q = useQuery({
    queryKey: ['attachments', parentKind, parentId, kind],
    queryFn: () => listAttachments(parentKind, parentId, kind),
  });
  const list = q.data ?? [];

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center"
      style={{ zIndex }}
    >
      <div
        className="bg-white rounded shadow-xl w-[480px] max-w-[92vw]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-slate-50 rounded-t">
          <h3 className="ui-subsection-title">{title ?? '📄 첨부 PDF 선택'}</h3>
          <button
            type="button"
            onClick={onClose}
            className="ui-close"
          >
            ×
          </button>
        </div>

        <div className="p-4">
          {q.isLoading && <p className="text-xs text-slate-500"><LoadingLines /></p>}
          {q.error && (
            <p className="text-xs text-rose-600">조회 실패: {(q.error as Error).message}</p>
          )}
          {!q.isLoading && list.length === 0 && (
            <p className="text-xs text-slate-500 italic">첨부된 파일이 없습니다.</p>
          )}
          {list.length > 0 && (
            <ul className="space-y-1">
              {list.map((a) => (
                <li
                  key={a.id}
                  className="flex items-center justify-between text-sm bg-slate-50 border border-slate-200 rounded px-3 py-2"
                >
                  <button
                    type="button"
                    onClick={() => previewAttachment(a)}
                    className="flex-1 text-left text-blue-700 hover:underline truncate"
                    title={a.filename}
                  >
                    📄 {a.filename}
                  </button>
                  <span className="text-xs text-slate-500 mx-2 whitespace-nowrap">
                    {Math.round(a.file_size / 1024)} KB
                  </span>
                  <button
                    type="button"
                    onClick={() => downloadAttachment(a.id, a.filename)}
                    className="btn btn-secondary btn-sm"
                    title="저장"
                  >
                    ⬇ 저장
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex justify-end px-5 py-3 border-t border-slate-200 bg-slate-50 rounded-b">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary btn-sm"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}
